// In-memory observable for the health of the local save path.
//
// Every mutating write funnels through `saveFullState` in persistence.ts. When
// a write fails (most importantly `QuotaExceededError` when the device is out
// of storage) the auto-save in the tracker is fire-and-forget, so the rejection
// would otherwise be invisible and the user would keep knitting while nothing
// persists. This store lets `saveFullState` report success/failure so a global
// banner can warn the user and point them at an export/backup.
//
// Mirrors the subscribe/get shape of lastSaved.ts so the React side consumes it
// the same way.

export type SaveErrorKind = 'quota' | 'unknown';

export type SaveStatus = { ok: true } | { ok: false; kind: SaveErrorKind };

let status: SaveStatus = { ok: true };

type Callback = () => void;
const callbacks: Callback[] = [];

function emit(): void {
  callbacks.forEach((cb) => cb());
}

export function getSaveStatus(): SaveStatus {
  return status;
}

export function subscribeSaveStatus(cb: Callback): () => void {
  callbacks.push(cb);
  return () => {
    const idx = callbacks.indexOf(cb);
    if (idx >= 0) callbacks.splice(idx, 1);
  };
}

/** Record that a save succeeded. Clears any prior error and notifies on change. */
export function markSaveOk(): void {
  if (status.ok) return;
  status = { ok: true };
  emit();
}

/** Record that a save failed. Notifies when the status or error kind changes. */
export function markSaveError(kind: SaveErrorKind): void {
  if (!status.ok && status.kind === kind) return;
  status = { ok: false, kind };
  emit();
}

// Test-only: reset module state between tests.
export function __resetSaveStatusForTest(): void {
  status = { ok: true };
  callbacks.length = 0;
}
